/* HeaderBar styles - Two-row layout */

/* ============================================
   MAIN CONTAINER - Column layout for two rows
   ============================================ */
.headerWrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  padding-top: 16px;
  padding-bottom: 4px;
  min-height: 110px;
  position: relative;
  z-index: 1;
  margin-bottom: 4px;
}

:global(.dark) .headerWrapper {
  color: var(--foreground);
  background-color: var(--background);
}

/* ============================================
   TOP ROW - SummaryCards | DatePicker | (empty)
   ============================================ */
.topRow {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

/* Left section: Summary cards - 50% width, wraps timer below when narrow */
.topLeft {
  flex: 0 1 50%;
  max-width: 550px;
  min-width: 0;
}

/* Middle section: DateRangePicker */
.topMiddle {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
}

/* Right section: Timer + MatterSelector stacked, left-aligned */
.topRight {
  flex: 0 1 40%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

/* Timer control - fill parent width */
.timerControl {
  width: 100%;
}

.timerControl > div {
  width: 100%;
}

/* Matter selector control - fill parent width */
.matterControl {
  width: 100%;
  height: 40px;
}

/* Reset button sizing for matterControl */
.matterControl :global(button) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  min-width: unset;
  padding: revert !important;
  border-radius: revert !important;
}

/* ============================================
   BOTTOM ROW - ActionButtons+Matter | Timer+Add+Export
   ============================================ */
.bottomRow {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 48px;
}

/* Left section: Action buttons + DatePicker - shrinks to make room for Add/Export */
.bottomLeft {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

/* Right section: Add + Export - always inline, never wraps */
.bottomRight {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.actionButtons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

/*
 * ACTION BUTTON SIZING CONTRACT
 * ─────────────────────────────────────────────────────────────
 * All buttons inside .actionButtons automatically get:
 * - Height: 40px (h-10)
 * - Min-width: 40px (for icon-only buttons)
 * - Consistent padding and border-radius
 *
 * This ensures any new button added inherits the correct size.
 * Use .actionButtonWide for buttons with text labels.
 */

/* Base sizing for ALL buttons in the action bar */
.actionButtons :global(button) {
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  min-width: 40px;
  padding: 0 10px;
  border-radius: 6px;
}

/* Icon-only buttons get square sizing */
.actionButtonIcon {
  width: 40px !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Buttons with text labels get auto width with padding */
.actionButtonWide {
  width: auto;
  padding: 0 12px !important;
  white-space: nowrap;
}

/* Compact compound controls (MatterSelector, etc.) in action bar */
.compactControl {
  height: 40px;
  width: 320px;
}

/* Filter button - leftmost with 16px gap to action icons */
.filterButton {
  margin-right: 16px;
}

/* DateRangePicker inline with action buttons */
.datePickerInline {
  margin-left: 16px;
}

/* Reset inherited button rules for compound controls - restore original styling */
.compactControl :global(button) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  min-width: unset;
  padding: revert !important;       /* Restore original button padding */
  border-radius: revert !important; /* Restore original border-radius */
}

/* Group combine + duplicate together visually */
.modifyGroup {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ============================================
   DELETE BUTTON THEMING
   ============================================ */
.deleteButton {
  color: var(--destructive) !important;
  background-color: color-mix(in oklch, var(--destructive) 10%, transparent) !important;
  border: 1px solid color-mix(in oklch, var(--destructive) 50%, transparent) !important;
}

.deleteButton:hover {
  background-color: color-mix(in oklch, var(--destructive) 20%, transparent) !important;
}

/* No hover effect when disabled (no entries selected) */
.deleteButtonNoHover:hover {
  background-color: color-mix(in oklch, var(--destructive) 10%, transparent) !important;
}

/* Delete confirmation button - inherits height from .actionButtons button, adds theme */
.deleteConfirmButton {
	font-size: 0.8125rem;
	font-weight: 500;
	line-height: 1;
	color: var(--destructive);
	background-color: color-mix(in oklch, var(--destructive) 10%, transparent);
	border: 1px solid color-mix(in oklch, var(--destructive) 50%, transparent);
}

.deleteConfirmButton:hover {
  background-color: color-mix(in oklch, var(--destructive) 20%, transparent) !important;
}

/* ============================================
   CONTROL ITEMS (DateRangePicker, etc.)
   ============================================ */
.controlItem {
  width: 320px;
  max-width: 100%;
}

/* ============================================
   PRIMARY ACTIONS (Timer, Add, Export)
   ============================================ */
.primaryActions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

/* ============================================
   ICON STYLING (Timer icons)
   ============================================ */
.iconSmall {
  width: 16px;
  height: 16px;
  color: white;
  cursor: pointer;
}

/* ============================================
   RESPONSIVE STYLES - Tablet (narrow)
   ============================================ */
/* (974px breakpoint removed - bottom row now uses flex auto-sizing) */

/* ============================================
   RESPONSIVE STYLES - Intermediate
   Prevents header bar from merging with table
   ============================================ */
@media (max-width: 850px) {
  .headerWrapper {
    min-height: unset;
    gap: 6px;
    padding-top: 12px;
  }

  .topRow {
    gap: 6px;
  }

  .actionButtons {
    flex-wrap: wrap;
    gap: 4px;
  }

  .datePickerInline {
    margin-left: 8px;
  }

  .compactControl {
    width: 240px;
  }
}

/* ============================================
   RESPONSIVE STYLES - Small tablet
   ============================================ */
@media (max-width: 650px) {
  .compactControl {
    width: 100%;
  }
}

/* ============================================
   RESPONSIVE STYLES - Mobile
   ============================================ */
@media screen and (max-width: 500px) {
  .headerWrapper {
    gap: 12px;
    min-height: unset;
  }

  .topRow {
    gap: 8px;
  }

  .topMiddle {
    flex: 1 1 100%;
  }

  .topMiddle .controlItem {
    width: 100%;
  }

  .compactControl {
    width: 100%;
  }
}
